import React, { useEffect, useState, useRef } from 'react';
import Intercom from '@intercom/messenger-js-sdk';
import { onAuthStateChanged } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebaseConfig';

/**
 * Intercom Messenger Component with JWT Security
 * Only initializes for authenticated users
 * Uses JWT tokens for secure authentication with Intercom
 * Disabled on archive plan landing page (/archive)
 */
export default function IntercomMessenger() {
  const [user, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [jwtToken, setJwtToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const refreshTimerRef = useRef(null);
  const location = useLocation();

  // Check if current route is the archive page
  const isArchivePage = location.pathname === '/archive';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && !isInitialized && !isArchivePage) {
        setUser(currentUser);
        initializeIntercomWithJWT(currentUser);
      } else if (!currentUser && isInitialized) {
        // User logged out, shutdown Intercom
        shutdownIntercom();
      } else if (currentUser && isInitialized && isArchivePage) {
        // User navigated to archive page, shutdown Intercom
        shutdownIntercom();
      }
    });

    return () => {
      unsubscribe();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [isInitialized, isArchivePage]);

  // Handle route changes - shutdown/initialize Intercom based on current route
  useEffect(() => {
    if (user) {
      if (isArchivePage && isInitialized) {
        // Navigated to archive page, shutdown Intercom
        shutdownIntercom();
      } else if (!isArchivePage && !isInitialized) {
        // Navigated away from archive page, initialize Intercom
        initializeIntercomWithJWT(user);
      }
    }
  }, [isArchivePage, user, isInitialized]);

  const fetchJWTToken = async (currentUser) => {
    try {
      // Get the Firebase ID token
      const idToken = await currentUser.getIdToken();
      
      const response = await fetch('/api/generate-intercom-jwt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch JWT: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.jwt) {
        throw new Error('Invalid JWT response from server');
      }

      return {
        token: data.jwt,
        expiresIn: data.expiresIn || 3600 // Default to 1 hour
      };
    } catch (error) {
      console.error('❌ Error fetching Intercom JWT:', error);
      throw error;
    }
  };

  const scheduleTokenRefresh = (expiresIn) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Schedule refresh 5 minutes before expiry
    const refreshTime = Math.max((expiresIn - 300) * 1000, 60000); // At least 1 minute
    
    refreshTimerRef.current = setTimeout(async () => {
      console.log('🔄 Refreshing Intercom JWT token...');
      if (user) {
        try {
          await initializeIntercomWithJWT(user);
        } catch (error) {
          console.error('❌ Error refreshing Intercom JWT:', error);
        }
      }
    }, refreshTime);

    console.log(`⏰ JWT refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`);
  };

  const initializeIntercomWithJWT = async (currentUser) => {
    try {
      console.log('🔧 Initializing Intercom with JWT for user:', currentUser.uid);

      // Fetch JWT token from our secure backend
      const { token, expiresIn } = await fetchJWTToken(currentUser);
      
      setJwtToken(token);
      setTokenExpiry(Date.now() + (expiresIn * 1000));

      // Initialize Intercom with JWT
      Intercom({
        app_id: 'jz88d4b5',
        intercom_user_jwt: token,
        // Non-sensitive attributes can be included directly
        platform: 'web_app',
        user_agent: navigator.userAgent,
        session_duration: 86400000 // 1 day
      });

      setIsInitialized(true);
      
      // Schedule token refresh
      scheduleTokenRefresh(expiresIn);
      
      console.log('✅ Intercom initialized successfully with JWT authentication');
    } catch (error) {
      console.error('❌ Error initializing Intercom with JWT:', error);
      
      // Fallback: If JWT fails, we could optionally initialize without JWT
      // For security, it's better to not initialize at all if JWT fails
      console.warn('⚠️ Intercom not initialized due to JWT failure - this is expected for security');
    }
  };

  const shutdownIntercom = () => {
    try {
      // Clear token refresh timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      // Shutdown Intercom when user logs out
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
      
      // Reset all state
      setIsInitialized(false);
      setUser(null);
      setJwtToken(null);
      setTokenExpiry(null);
      
      console.log('🔄 Intercom shutdown for logged out user');
    } catch (error) {
      console.error('❌ Error shutting down Intercom:', error);
    }
  };

  // This component doesn't render anything visible
  // Intercom messenger appears automatically in bottom-right corner
  return null;
} 